/**
 * Transport Service - API client for vehicles, routes, enrollments,
 * attendance, fees, maintenance, and drivers
 */
import api from '../utils/api';

const transportService = {
  // ---- Vehicles ----

  listVehicles: async (params = {}) => {
    try {
      return await api.get('/api/transport/vehicles', { params });
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      throw error;
    }
  },

  getVehicleTypes: async () => {
    try {
      return await api.get('/api/transport/vehicle-types');
    } catch (error) {
      console.error('Error fetching vehicle types:', error);
      throw error;
    }
  },

  createVehicle: async (data) => {
    try {
      return await api.post('/api/transport/vehicles', data);
    } catch (error) {
      console.error('Error creating vehicle:', error);
      throw error;
    }
  },

  updateVehicle: async (vehicleId, data) => {
    try {
      return await api.put(`/api/transport/vehicles/${vehicleId}`, data);
    } catch (error) {
      console.error('Error updating vehicle:', error);
      throw error;
    }
  },

  getVehicleDeleteImpact: async (vehicleId) => {
    try {
      return await api.get(`/api/transport/vehicles/${vehicleId}/delete-impact`);
    } catch (error) {
      console.error('Error fetching vehicle delete impact:', error);
      throw error;
    }
  },

  deleteVehicle: async (vehicleId) => {
    try {
      return await api.delete(`/api/transport/vehicles/${vehicleId}`);
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      throw error;
    }
  },

  // ---- Routes ----

  listRoutes: async (params = {}) => {
    try {
      return await api.get('/api/transport/routes', { params });
    } catch (error) {
      console.error('Error fetching routes:', error);
      throw error;
    }
  },

  getRoute: async (routeId) => {
    try {
      return await api.get(`/api/transport/routes/${routeId}`);
    } catch (error) {
      console.error('Error fetching route:', error);
      throw error;
    }
  },

  createRoute: async (data) => {
    try {
      return await api.post('/api/transport/routes', data);
    } catch (error) {
      console.error('Error creating route:', error);
      throw error;
    }
  },

  updateRoute: async (routeId, data) => {
    try {
      return await api.put(`/api/transport/routes/${routeId}`, data);
    } catch (error) {
      console.error('Error updating route:', error);
      throw error;
    }
  },

  getRouteDeleteImpact: async (routeId) => {
    try {
      return await api.get(`/api/transport/routes/${routeId}/delete-impact`);
    } catch (error) {
      console.error('Error fetching route delete impact:', error);
      throw error;
    }
  },

  deleteRoute: async (routeId) => {
    try {
      return await api.delete(`/api/transport/routes/${routeId}`);
    } catch (error) {
      console.error('Error deleting route:', error);
      throw error;
    }
  },

  getRouteStudents: async (routeId) => {
    try {
      return await api.get(`/api/transport/routes/${routeId}/students`);
    } catch (error) {
      console.error('Error fetching route students:', error);
      throw error;
    }
  },

  // ---- Enrollments ----

  listEnrollments: async (params = {}) => {
    try {
      return await api.get('/api/transport/enrollments', { params });
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      throw error;
    }
  },

  createEnrollment: async (data) => {
    try {
      return await api.post('/api/transport/enrollments', data);
    } catch (error) {
      console.error('Error creating enrollment:', error);
      throw error;
    }
  },

  deleteEnrollment: async (enrollmentId) => {
    try {
      return await api.delete(`/api/transport/enrollments/${enrollmentId}`);
    } catch (error) {
      console.error('Error deleting enrollment:', error);
      throw error;
    }
  },

  // ---- Attendance ----

  getRouteAttendance: async (routeId, params = {}) => {
    try {
      return await api.get(`/api/transport/attendance/${routeId}`, { params });
    } catch (error) {
      console.error('Error fetching route attendance:', error);
      throw error;
    }
  },

  bulkMarkAttendance: async (records) => {
    try {
      return await api.post('/api/transport/attendance/bulk', { attendance_records: records });
    } catch (error) {
      console.error('Error submitting attendance:', error);
      throw error;
    }
  },

  // ---- Fees ----

  listFees: async (params = {}) => {
    try {
      return await api.get('/api/transport/fees', { params });
    } catch (error) {
      console.error('Error fetching transport fees:', error);
      throw error;
    }
  },

  createFee: async (data) => {
    try {
      return await api.post('/api/transport/fees', data);
    } catch (error) {
      console.error('Error creating transport fee:', error);
      throw error;
    }
  },

  updateFee: async (feeId, data) => {
    try {
      return await api.put(`/api/transport/fees/${feeId}`, data);
    } catch (error) {
      console.error('Error updating transport fee:', error);
      throw error;
    }
  },

  getStudentFees: async (studentId) => {
    try {
      return await api.get(`/api/transport/fees/${studentId}`);
    } catch (error) {
      console.error('Error fetching student transport fees:', error);
      throw error;
    }
  },

  // ---- Maintenance ----

  listMaintenance: async () => {
    try {
      return await api.get('/api/transport/maintenance');
    } catch (error) {
      console.error('Error fetching maintenance logs:', error);
      throw error;
    }
  },

  createMaintenance: async (data) => {
    try {
      return await api.post('/api/transport/maintenance', data);
    } catch (error) {
      console.error('Error creating maintenance log:', error);
      throw error;
    }
  },

  // ---- Drivers ----

  listDrivers: async (params = {}) => {
    try {
      return await api.get('/api/transport/drivers', { params });
    } catch (error) {
      console.error('Error fetching drivers:', error);
      throw error;
    }
  },

  createDriver: async (data) => {
    try {
      return await api.post('/api/transport/drivers', data);
    } catch (error) {
      console.error('Error registering driver:', error);
      throw error;
    }
  },

  updateDriver: async (driverId, data) => {
    try {
      return await api.put(`/api/transport/drivers/${driverId}`, data);
    } catch (error) {
      console.error('Error updating driver:', error);
      throw error;
    }
  },

  verifyDriver: async (driverId, verificationNotes) => {
    try {
      return await api.put(`/api/transport/drivers/${driverId}/verify`, null, {
        params: { verification_notes: verificationNotes }
      });
    } catch (error) {
      console.error('Error verifying driver:', error);
      throw error;
    }
  },

  getDriverDeleteImpact: async (driverId) => {
    try {
      return await api.get(`/api/transport/drivers/${driverId}/delete-impact`);
    } catch (error) {
      console.error('Error fetching driver delete impact:', error);
      throw error;
    }
  },

  deleteDriver: async (driverId) => {
    try {
      return await api.delete(`/api/transport/drivers/${driverId}`);
    } catch (error) {
      console.error('Error deleting driver:', error);
      throw error;
    }
  }
};

export default transportService;
